import { useRef, useState } from 'react';
import { Download, Eye, FileText, Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { api } from '@/lib/api';
import {
  fechaCorta,
  formatearTamano,
  TIPO_DOCUMENTO_LABEL,
  type Dispositivo,
  type DocumentoDispositivo,
  type TipoDocumento,
} from '@/lib/dispositivos';

/**
 * La factura de compra, una foto de la instalación, la etiqueta con el número
 * de serie. Se sube arrastrando o desde el selector, y en el móvil con la
 * cámara: la etiqueta se fotografía en el sitio, no se escanea.
 */
interface Props {
  dispositivo: Dispositivo;
  onActualizado: (d: Dispositivo) => void;
}

const TIPOS = Object.keys(TIPO_DOCUMENTO_LABEL) as TipoDocumento[];

export default function DocumentosDispositivo({ dispositivo, onActualizado }: Props) {
  const [tipo, setTipo] = useState<TipoDocumento>('factura');
  const [subiendo, setSubiendo] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const camaraRef = useRef<HTMLInputElement>(null);

  const documentos = [...(dispositivo.documentos || [])].sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));

  const subir = async (files: FileList | File[] | null) => {
    const lista = Array.from(files || []);
    if (!lista.length) return;
    setSubiendo(true);
    setError(null);
    try {
      let ultimo = dispositivo;
      for (const f of lista) {
        const { dispositivo: actualizado } = await api.subirDocumentoDispositivo(dispositivo.id, f, tipo);
        ultimo = actualizado;
      }
      onActualizado(ultimo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido subir el archivo');
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = '';
      if (camaraRef.current) camaraRef.current.value = '';
    }
  };

  const borrar = async (doc: DocumentoDispositivo) => {
    if (!confirm(`¿Eliminar "${doc.fileName}"? No se puede deshacer.`)) return;
    setOcupado(doc.id);
    setError(null);
    try {
      const { dispositivo: actualizado } = await api.borrarDocumentoDispositivo(dispositivo.id, doc.id);
      onActualizado(actualizado);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido borrar');
    } finally {
      setOcupado(null);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Documentos</h3>
        <span className="text-xs text-sereno-dark/40">{documentos.length}</span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {TIPOS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTipo(t)}
            className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
              tipo === t ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-sereno-dark/60 hover:bg-gray-200'
            }`}
          >
            {TIPO_DOCUMENTO_LABEL[t]}
          </button>
        ))}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastrando(false); void subir(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          arrastrando ? 'border-sereno-dark bg-sereno-blue/20' : 'border-gray-200'
        }`}
      >
        {subiendo ? (
          <span className="inline-flex items-center gap-2 text-sm text-sereno-dark/50">
            <Loader2 size={16} className="animate-spin" /> Subiendo…
          </span>
        ) : (
          <>
            <p className="text-sm text-sereno-dark/50 mb-2">Arrastra aquí, o</p>
            <div className="flex items-center justify-center gap-2">
              <button type="button" onClick={() => inputRef.current?.click()} className="btn-secondary text-xs">
                <Upload size={14} className="inline mr-1" /> Elegir archivo
              </button>
              <button type="button" onClick={() => camaraRef.current?.click()} className="btn-secondary text-xs sm:hidden">
                Hacer foto
              </button>
            </div>
          </>
        )}
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => void subir(e.target.files)} />
        <input ref={camaraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void subir(e.target.files)} />
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <div className="mt-4 divide-y divide-gray-50">
        {documentos.length === 0 ? (
          <p className="text-sm text-sereno-dark/40 py-3">Todavía no hay ningún documento.</p>
        ) : (
          documentos.map((doc) => {
            const I = (doc.contentType || '').startsWith('image/') ? ImageIcon : FileText;
            return (
              <div key={doc.id} className="flex items-center gap-3 py-2.5">
                <I size={16} className="text-sereno-dark/40 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{doc.fileName}</p>
                  <p className="text-xs text-sereno-dark/40">
                    {TIPO_DOCUMENTO_LABEL[doc.tipo] || doc.tipo}
                    {doc.size ? ` · ${formatearTamano(doc.size)}` : ''}
                    {doc.uploadedAt ? ` · ${fechaCorta(doc.uploadedAt)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <a href={api.urlDocumentoDispositivo(dispositivo.id, doc.id)} target="_blank" rel="noopener noreferrer" className="p-1.5 text-sereno-dark/40 hover:text-sereno-dark transition-colors" title="Ver">
                    <Eye size={15} />
                  </a>
                  <a href={api.urlDocumentoDispositivo(dispositivo.id, doc.id, true)} className="p-1.5 text-sereno-dark/40 hover:text-sereno-dark transition-colors" title="Descargar">
                    <Download size={15} />
                  </a>
                  <button type="button" onClick={() => void borrar(doc)} disabled={ocupado === doc.id} className="p-1.5 text-sereno-dark/40 hover:text-red-600 transition-colors disabled:opacity-50" title="Borrar">
                    {ocupado === doc.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
